import React, { useCallback, useContext, useMemo, useState } from "react";
import _ from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "reflexbox";
import * as yup from "yup";
import Loading from "../UI/Loading";
import { PageWrapper } from "../UI/Grid";
import { useQuery } from "../Hooks";
import * as platforms from "../../requests/platformManagementRequests";
import { getCustomers } from "../../requests/customerManagementRequests";
import UserContext from "../../context/user/UserContext";
import SocketContext from "../../context/socket/SocketContext";
import HeadingSection from "../UI/HeadingSection";
import { Label } from "../UI/forms";
import { Flex } from "reflexbox";
import Card from "../UI/Card";
import { useTheme } from "styled-components";
import * as importManagementRequests from "../../requests/importManagementRequests";
import { File } from "../UI/forms";
import Button from "../UI/Button";
import AlertContext from "../../context/alert/AlertContext";
import { Body } from "../UI/Text";
import Table from "../UI/Table";
import Hover from "../UI/Hover";
import styled from "styled-components";
import { Icon } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { Heading } from "../UI/Text";
import Field from "../UI/forms/Field";
import { Form, Formik } from "formik";

const DATA_TYPES = ["Products", "Customers", "Address", "Users"];

const ImportItem = () => {
  const theme = useTheme();
  const { dataType } = useParams();
  const [uploadDisabled, setDisabled] = useState(false);
  const { showSuccessAlert, showFailureAlert } = useContext(AlertContext);
  const { pid, activeUser, isLoading: userLoading } = useContext(UserContext);
  const { roleLevel } = activeUser;
  const {
    downloadCustomerTemplate,
    downloadProductTemplate,
    downloadAddressTemplate,
    downloadUserTemplate,
  } = importManagementRequests;
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState("");
  const { data: customers, isLoading: customersLoading } = useQuery({
    request: getCustomers,
    queryVariables: { pid },
    forcedFilters: {},
    skip: userLoading,
  });

  const downloadTemplate = useCallback(async () => {
    if (dataType === "Products") {
      await downloadProductTemplate();
    } else if (dataType === "Customers") {
      await downloadCustomerTemplate();
    } else if (dataType === "Address") {
      await downloadAddressTemplate();
    } else if (dataType === "Users") {
      await downloadUserTemplate();
    }
  }, [
    dataType,
    downloadProductTemplate,
    downloadCustomerTemplate,
    downloadAddressTemplate,
    downloadUserTemplate,
  ]);

  const uploadPath = useMemo(() => {
    if (dataType === "Products") {
      return "/api/v1/importManagement/importProducts";
    } else if (dataType === "Customers") {
      return "/api/v1/importManagement/importCustomers";
    } else if (dataType === "Address") {
      return "/api/v1/importManagement/importAddress";
    } else if (dataType === "Users") {
      return "/api/v1/importManagement/importUsers";
    }
  }, [dataType]);

  const postUploadCallback = useCallback(
    async (response) => {
      setDisabled(false);
      const {
        data: { result, success, error },
      } = response;
      if (success) {
        const [validated, msg] = result;
        if (validated) {
          setValidationErrors("");
          showSuccessAlert(msg);
        } else {
          setValidationErrors(msg);
          showFailureAlert("Form failed to validate, see issues below");
        }
      } else {
        //setValidationErrors({});
        console.log(response);
        showFailureAlert("An error occured while uploading");
      }
    },
    [setDisabled, showSuccessAlert, showFailureAlert]
  );

  const formattedDataType = useMemo(
    () => (dataType === "Address" ? "Shipping Address" : dataType),
    [dataType]
  );

  const options = useMemo(
    () =>
      !!customers?.rows && customers?.rows?.length > 0
        ? [
            ...customers.rows.map(({ customer, contact, billingAddress }) => {
              const { id, companyName } = customer;
              return <option value={id}>{companyName}</option>;
            }),
          ]
        : [],
    [customers]
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        customerId: yup.string().nullable(),
      }),
    []
  );

  const initialValues = useMemo(() => ({ customerId: "" }), []);

  const handleSubmit = (values) => {
    console.log(values);
  };

  if (!DATA_TYPES.includes(dataType)) {
    navigate("/Imports");
    return null;
  }
  return customersLoading || userLoading ? (
    <Loading />
  ) : (
    <>
      <PageWrapper>
        <HeadingSection mt="1rem" hideCreate title={formattedDataType} />
        <Button
          mt="1rem"
          marginBottom="20px"
          reverse
          color={theme.primary}
          onClick={downloadTemplate}
        >
          <Body>Download {formattedDataType} Template</Body>
        </Button>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <>
              <Form>
                <>
                  {dataType === "Address" ? (
                    <Field
                      autoComplete="off"
                      name="customerId"
                      labelText="Enter a Customer to Assign Address:"
                      typeableSelect
                      options={options}
                    />
                  ) : null}
                </>
              </Form>
              <Flex>
                <Flex mt="1rem" width={1} alignItems="flex-start">
                  <File
                    id={dataType === "Address" ? +values.customerId : pid}
                    labelText={"Upload Template"}
                    path={uploadPath}
                    onSubmit={() => showSuccessAlert()}
                    name="img"
                    disabled={uploadDisabled}
                    preUploadCallback={() => setDisabled(true)}
                    postUploadCallback={postUploadCallback}
                    query={dataType === "Users" ? { roleLevel } : {}}
                  />
                </Flex>
              </Flex>
            </>
          )}
        </Formik>
        {validationErrors !== "" && (
          <ErrorLog dataType={dataType} errors={validationErrors} />
        )}
      </PageWrapper>
    </>
  );
};

const PRODUCT_FIELDS = [
  ["Name", "Name"],
  ["Part #", "ProductNumber"],
  ["Description", "Description"],
  ["Header", "HeaderId"],
  ["App. Instance", "ApplicationInstance"],
  ["App. Name", "ApplicationName"],
  ["Profile", "Profile"],
  ["Ruleset", "Ruleset"],
  ["Namespace", "Namespace"],
  ["Server", "Server"],
  ["Stock", "quantityInStock"],
  ["Price", "defaultPrice"],
  ["Related", "RelatedItem"],
];

const CUSTOMER_FIELDS = [
  ["Company", "companyName"],
  ["ERP ID", "erpId"],
  ["Description", "description"],
  ["First", "firstName"],
  ["Last", "lastName"],
  ["Email", "email"],
  ["Phone", "phone"],
  ["Address 1", "Address"],
  ["Address 2", "Address2"],
  ["City", "City"],
  ["State", "State"],
  ["Zip", "Zip"],
];

const ADDRESS_FIELDS = [
  ["Address 1", "Address"],
  ["Address 2", "Address2"],
  ["City", "City"],
  ["State", "State"],
  ["Zip", "Zip"],
];

const USER_FIELDS = [
  ["Email Address", "username"],
  ["First Name", "firstName"],
  ["Last Name", "lastName"],
  ["User Role", "roleId"],
  ["Checkout Type (order or stripe)", "checkoutType"],
  ["Password", "password"],
];

const StyledErrorTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0px;
  & td,
  th {
    position: relative;
    border-bottom: 1px solid ${({ theme }) => theme.borderColor};
    border-right: 1px solid ${({ theme }) => theme.borderColor};
    padding: 8px;
  }
  & th {
    background-color: ${({ theme }) => theme.backgroundColor};
    border-bottom: 3px solid ${({ theme }) => theme.borderColor};
    border-top: 1px solid ${({ theme }) => theme.borderColor};
    padding-top: 12px;
    padding-bottom: 12px;
    text-align: left;
  }
  & tbody tr:nth-child(even) {
    background-color: ${({ theme }) => theme.gridRowBackgroundColor};
  }
  & tbody tr:nth-child(odd) {
    background-color: ${({ theme }) => theme.backgroundColor};
  }
  & svg {
    color: ${({ theme }) => theme.danger};
  }
  & td:first-of-type,
  th {
    text-align: center;
  }
  & tbody tr:nth-child(even) td:first-of-type {
    border-right: none;
  }
  & td:first-of-type,
  th:first-of-type {
    border-left: 1px solid ${({ theme }) => theme.borderColor};
  }
  & th:first-of-type {
    border-top-left-radius: ${({ theme }) => theme.borderRadius}px;
  }
  & th:last-of-type {
    border-top-right-radius: ${({ theme }) => theme.borderRadius}px;
  }
  & tr:last-of-type td:first-of-type {
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius}px;
  }
  & tr:last-of-type td:last-of-type {
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius}px;
  }
  & .highlight {
    position: absolute;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    background: ${({ theme }) => theme.warning};
    opacity: 0.4;
  }
  & td div {
    position: relative;
    z-index: 1;
  }
`;

const ErrorLog = ({ dataType, errors }) => {
  console.log(errors);
  const theme = useTheme();
  const fields = useMemo(() => {
    switch (dataType) {
      case "Products":
        return PRODUCT_FIELDS;
      case "Customers":
        return CUSTOMER_FIELDS;
      case "Address":
        return ADDRESS_FIELDS;
      case "Users":
        return USER_FIELDS;
      default:
        console.log("This shouldn't run");
        return PRODUCT_FIELDS;
    }
  });
  return (
    <>
      <Body bold marginTop="1rem" marginBottom="1rem">
        Please fix the following the errors in your spreadsheet and reupload
        file to import items:
      </Body>
      {typeof errors === "string" ? (
        <StyledErrorTable>
          <thead>
            <tr>
              <th colSpan={2}>Formatting Errors</th>
            </tr>
          </thead>
          <tbody>
            <span />
            <tr>
              <td>
                <FontAwesomeIcon icon={faExclamationCircle} />
              </td>
              <td>
                <Body>{errors}</Body>
              </td>
            </tr>
          </tbody>
        </StyledErrorTable>
      ) : (
        <StyledErrorTable>
          <thead>
            <tr>
              <th>Row</th>
              {fields.map(([sheetField]) => (
                <th key={sheetField}>{sheetField}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {errors.map(([row, errors, data]) => (
              <>
                <tr key={row + "+"}>
                  <td>{row}</td>
                  {fields.map(([, dbField]) => (
                    <td key={dbField}>
                      <span
                        className={
                          typeof errors === "object" && dbField in errors
                            ? "highlight"
                            : ""
                        }
                      />
                      <Body>{data[dbField]}</Body>
                    </td>
                  ))}
                </tr>
                <tr key={row + "-"}>
                  <td>
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                    ></FontAwesomeIcon>
                  </td>
                  <td colSpan={fields.length}>
                    {typeof errors === "string" ? (
                      <Body>{errors}</Body>
                    ) : (
                      Object.values(errors).map((err, i) => (
                        <Body key={i}>{err}</Body>
                      ))
                    )}
                  </td>
                </tr>
              </>
            ))}
          </tbody>
        </StyledErrorTable>
      )}
    </>
  );
};

/* 
const ErrorField = ({field, error, data}) => {
  const errorExists = (typeof error) === 'object' && field in error;
  
  return (
    <Hover hoverText={errorExists ? error[field] : null} highlight={errorExists}>
      {data[field]}
    </Hover>
  )
} */

export default ImportItem;
